import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import MainLayout from "layouts/Main";
import { Box } from "@mui/material";
import { useAppDispatch, useAppSelector } from "hooks";
import { selectForecast } from "store/selectors";
import { fetchForecast } from "store/appSlice";
import {
  confirmForecast,
  unConfirmForecast,
  updateForecast,
} from "api/resources/forecasts";
import Filters from "../../Filters";
import {
  Forecast,
  ForecastPeriodConsumption,
  ForecastPeriodData,
  ForecastTemperatureMatch,
  RowValue,
  SelectedHour,
} from "api/models/Forecast";
import { BLOCK_ORDER, TableRow } from "./ForecastDataGrid";
import ForecastActions from "../../Components/ForecastActions";
import Header from "../../Components/Header";
import ForecastDetails from "../../Components/ForecastDetails";
import MainContent from "../../Components/ForecastMainContent";
import { ProtectedPaths } from "routes";
import { Client } from "api/models/Client";
import { MeteringPoint } from "api/models/MeteringPoint";

interface FormValues {
  customDates: string[];
  selectedClients: Client[];
  selectedMeteringPointsOptions: MeteringPoint[];
}

interface GroupedCells {
  [date: string]: (ForecastPeriodConsumption | null)[];
}

interface ConsumptionPeriodData {
  date: string;
  isoDate: string;
  day: string;
  readings: ForecastPeriodConsumption[];
}

type CellKey = `${string} ${string}:${string}`;

const Reports = () => {
  const [periodsData, setPeriodsData] = useState<ForecastPeriodData[]>([]);
  const [averageLine, setAverageLine] = useState<number[]>([]);
  const [snackbar, setSnackbar] = useState<{
    type: "success" | "error" | "info";
    message: string;
  } | null>(null);
  const [forecastLine, setForecastLine] = useState<number[]>([]);
  const [saving, setSaving] = useState<boolean>(false);
  const [updating, setUpdating] = useState<boolean>(false);
  const { sampleId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const forecast = useAppSelector(selectForecast);
  const [formValues, setFormValues] = useState({
    customDates: [],
    selectedClients: [],
    selectedMeteringPointsOptions: [],
  });
  const [tableMatrix, setTableMatrix] = useState<any[]>([]);
  const [isEditable, setIsEditable] = useState(false);

  useEffect(() => {
    dispatch(fetchForecast(sampleId));
  }, [dispatch, sampleId]);

  useEffect(() => {
    if (forecast && forecast.id === Number(sampleId)) {
      setIsEditable(new Date(forecast.prognoseDate) >= new Date());

      const newFormValues = getUpdatedFormValues(forecast, formValues);
      setFormValues(newFormValues);

      const groupedCells = getGroupedCells(forecast.cellsJson);

      const periodsDataInitial = sortPeriodsData(
        createPeriodsData(groupedCells),
        forecast.temperatureMatches ?? [],
      );

      setPeriodsData(periodsDataInitial);
    }
  }, [forecast]);

  const getUpdatedFormValues = (forecast: Forecast, formValues: FormValues) => {
    const newFormValues = { ...formValues };
    newFormValues.customDates = forecast.customDates || [];
    newFormValues.selectedClients = (
      forecast.filtersJson[0].clients || []
    ).filter((client) => client != null);
    newFormValues.selectedMeteringPointsOptions =
      forecast.filtersJson[0].meteringPoints || [];
    return newFormValues;
  };

  const getGroupedCells = (
    cellsJson: Record<CellKey, ForecastPeriodConsumption>,
  ) => {
    const groupedCells: {
      [key: string]: (ForecastPeriodConsumption | null)[];
    } = {};

    const hourlyForecast = dayjs(forecast.prognoseDate).isBefore("2025-09-06");

    Object.entries(cellsJson).forEach(([key, cell]) => {
      const [date, time] = key.split(" ");

      if (!groupedCells[date]) {
        groupedCells[date] = new Array(25).fill(null);
      }

      const [hours, minutes] = time.split(":").map(Number);
      const index = hourlyForecast ? hours : (hours * 60 + minutes) / 15;

      groupedCells[date][index] = cell;
    });

    return groupedCells;
  };

  const createPeriodsData = (
    groupedCells: GroupedCells,
  ): ConsumptionPeriodData[] => {
    return Object.entries(groupedCells).map(([date, cells]) => {
      const readings: ForecastPeriodConsumption[] = cells.map((cell) => {
        return {
          datetime: `${cell.datetime}`,
          selected: cell?.selected || false,
          totalConsumption: cell?.totalConsumption || 0,
          isMissingData: cell
            ? cell.meteringPointsWithConsumption < cell.meteringPointsTotal
            : false,
          meteringPointsWithConsumption:
            cell?.meteringPointsWithConsumption || 0,
          meteringPointsTotal: cell?.meteringPointsTotal || 0,
          selectedBasedOnTemperature: cell?.selectedBasedOnTemperature || false,
          selectable: cell?.selectable ?? true,
        };
      });

      return {
        date: dayjs(date).format("DD.MM.YYYY"),
        isoDate: date,
        day: new Date(date).toLocaleDateString("en-US", { weekday: "long" }),
        readings,
      };
    });
  };

  /**
   * The API hands the days over in whatever order it assembled them, which reads as random once
   * temperature-matched days months back are mixed in. Order them the way they are reasoned about: the
   * days the calendar rule picked first, then the temperature matches by the block they won.
   *
   * A legacy day is togglable across its whole length, a day that got in only through temperature
   * matching just inside the block it matched on — so `selectable` is what separates the two.
   */
  const sortPeriodsData = (
    days: ConsumptionPeriodData[],
    matches: ForecastTemperatureMatch[],
  ): ConsumptionPeriodData[] => {
    const isLegacy = (day: ConsumptionPeriodData) =>
      day.readings.every((reading) => reading.selectable);

    const matchesFor = (isoDate: string) =>
      matches.filter((match) => match.date === isoDate);

    // A day can win several blocks; it belongs under the earliest one, the same way its header labels
    // them "Night + Evening".
    const earliestBlock = (isoDate: string) =>
      Math.min(
        ...matchesFor(isoDate).map((match) => BLOCK_ORDER.indexOf(match.block)),
      );

    const bestRank = (isoDate: string) =>
      Math.min(...matchesFor(isoDate).map((match) => match.matchRank));

    const legacyDays = days.filter(isLegacy);
    const temperatureDays = days.filter((day) => !isLegacy(day));

    temperatureDays.sort(
      (a, b) =>
        earliestBlock(a.isoDate) - earliestBlock(b.isoDate) ||
        bestRank(a.isoDate) - bestRank(b.isoDate) ||
        a.isoDate.localeCompare(b.isoDate),
    );

    return [...legacyDays, ...temperatureDays];
  };

  const handleSubmitFilters = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const forecastData = {
      customDates: formValues.customDates,
      filters: [
        {
          clients: formValues.selectedClients,
          meteringPoints: formValues.selectedMeteringPointsOptions,
        },
      ],
    };

    updateForecast(sampleId, forecastData)
      .then(() => {
        dispatch(fetchForecast(sampleId));
        setSnackbar({
          type: "success",
          message: "Filters saved",
        });
      })
      .catch((error) => {
        console.log({ error });
        setSnackbar({
          type: "error",
          message: "Something went wrong when saving the forecast",
        });
      });
  };

  const getForecastValues = () => {
    const rowValues: RowValue[] = [];
    const selectedHours: SelectedHour[] = [];

    tableMatrix.forEach((row: TableRow) => {
      if (!row.repeatedHour) {
        row.readings.forEach((day, dayIndex) => {
          const time =
            periodsData[dayIndex].readings[row.readingIndex].datetime;

          selectedHours.push({
            [time]: day.active || false,
          });
        });
      }

      rowValues.push({
        hour: row.hour,
        minute: row.minute,
        repeatedHour: row.repeatedHour,
        adjustment: Number(row.correction),
        value: row.forecast,
      });
    });

    return {
      prognoseValues: rowValues,
      selectedPeriods: selectedHours,
    };
  };

  const handleSaveForecast = () => {
    setSaving(true);

    updateForecast(sampleId, getForecastValues())
      .then(() => {
        setSaving(false);
        navigate(`/${ProtectedPaths.Consumption}`);
      })
      .catch((error) => {
        console.log({ error });
        setSaving(false);
        setSnackbar({
          type: "error",
          message: "Something went wrong when saving the forecast",
        });
      });
  };

  // The API reloads the metering data on every update; the saved selections are sent back so they survive it.
  const handleUpdateForecastData = () => {
    setUpdating(true);

    const selectedPeriods = Object.entries(forecast.cellsJson).map(
      ([datetime, cell]) => ({ [datetime]: cell.selected }),
    );

    updateForecast(sampleId, { selectedPeriods })
      .then(() => {
        dispatch(fetchForecast(sampleId));
        setSnackbar({
          type: "success",
          message: "Forecast data updated",
        });
      })
      .catch((error) => {
        console.log({ error });
        setSnackbar({
          type: "error",
          message: "Something went wrong when updating the forecast data",
        });
      })
      .finally(() => setUpdating(false));
  };

  const handleConfirmForecast = () => {
    confirmForecast(Number(sampleId)).then(() => {
      setSnackbar({
        type: "success",
        message: "Forecast confirmed",
      });
      dispatch(fetchForecast(sampleId));
    });
  };

  const handleUnconfirmForecast = () => {
    unConfirmForecast(sampleId).then(() => {
      setSnackbar({
        type: "info",
        message: "Forecast opened for changes",
      });
      dispatch(fetchForecast(sampleId));
    });
  };

  const snackbarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setSnackbar(null);
  };

  if (!forecast || forecast.id !== Number(sampleId)) return null;

  return (
    <MainLayout
      tabs={[
        { label: "Manage samples", path: "Consumption" },
        { label: "Saved samples", path: "SavedSamples" },
      ]}
      snackbar={{
        open: !!snackbar,
        handleClose: snackbarClose,
        type: snackbar?.type,
        message: snackbar?.message,
      }}
    >
      <Box
        display={"flex"}
        flexDirection={"row"}
        justifyContent={"space-between"}
      >
        <Header type="consumption" forecast={forecast} />
        <ForecastActions
          isEditable={isEditable}
          isVerified={forecast.isVerified}
          handleConfirmForecast={handleConfirmForecast}
          handleSaveForecast={handleSaveForecast}
          handleUnconfirmForecast={handleUnconfirmForecast}
          handleUpdateProductionPrognosisData={handleUpdateForecastData}
          saving={saving}
          updating={updating}
        />
      </Box>
      {isEditable && (
        <Filters
          formValues={formValues}
          setFormValues={setFormValues}
          handleSubmit={handleSubmitFilters}
          submitText="Save filters"
          showButtons={true}
          loading={false}
        />
      )}
      <ForecastDetails forecast={forecast} />
      <MainContent
        periodsData={periodsData}
        setPeriodsData={setPeriodsData}
        setAverageLine={setAverageLine}
        setForecastLine={setForecastLine}
        tableMatrix={tableMatrix}
        setTableMatrix={setTableMatrix}
        isEditable={isEditable}
        forecast={forecast}
        averageLine={averageLine}
        forecastLine={forecastLine}
        hourlyForecast={dayjs(forecast.prognoseDate).isBefore("2025-09-06")}
      />
    </MainLayout>
  );
};

export default Reports;
